import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  deleteLocatorDataFile,
  fetchLocatorDataFile,
  fetchLocatorDataFiles,
} from '../../services/locatorDriftService';
import { errorMessage } from './locatorDriftUtils';

export const loadLocatorHistoryAsync = createAsyncThunk(
  'locatorDrift/history/loadHistory',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchLocatorDataFiles();
      return response;
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  },
);

export const loadLocatorFileAsync = createAsyncThunk(
  'locatorDrift/history/loadFile',
  async (runId, { rejectWithValue }) => {
    try {
      return {
        runId,
        data: await fetchLocatorDataFile(runId),
      };
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  },
);

export const loadLatestLocatorResultAsync = createAsyncThunk(
  'locatorDrift/history/loadLatest',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const files = await fetchLocatorDataFiles();
      if (!Array.isArray(files) || files.length === 0) {
        return { files: [], runId: null, data: null };
      }

      const runId = files[0].run_id;

      const result = await dispatch(loadLocatorFileAsync(runId));
      if (loadLocatorFileAsync.rejected.match(result)) {
        throw new Error(result.payload || result.error?.message);
      }

      return {
        files,
        runId,
        data: result.payload.data,
      };
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  },
);

export const deleteLocatorHistoryFileAsync = createAsyncThunk(
  'locatorDrift/history/deleteHistoryFile',
  async (filename, { dispatch, rejectWithValue }) => {
    try {
      await deleteLocatorDataFile(filename);
      await dispatch(loadLocatorHistoryAsync());
      return filename;
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  },
);

const locatorHistorySlice = createSlice({
  name: 'locatorDriftHistory',
  initialState: {
    historyFiles: [],
    historyLoading: false,
    historyError: null,
    activeRunId: null,
    latestResult: null,
    resultLoading: false,
    resultError: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadLocatorHistoryAsync.pending, (state) => {
        state.historyLoading = true;
        state.historyError = null;
      })
      .addCase(loadLocatorHistoryAsync.fulfilled, (state, action) => {
        state.historyLoading = false;
        state.historyFiles = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(loadLocatorHistoryAsync.rejected, (state, action) => {
        state.historyLoading = false;
        state.historyError = action.payload;
      })
      .addCase(loadLocatorFileAsync.pending, (state) => {
        state.resultLoading = true;
        state.resultError = null;
      })
      .addCase(loadLocatorFileAsync.fulfilled, (state, action) => {
        state.resultLoading = false;
        state.activeRunId = action.payload.runId;
        state.latestResult = action.payload.data;
      })
      .addCase(loadLocatorFileAsync.rejected, (state, action) => {
        state.resultLoading = false;
        state.resultError = action.payload;
      })
      .addCase(loadLatestLocatorResultAsync.fulfilled, (state, action) => {
        state.historyFiles = action.payload.files;
        state.activeRunId = action.payload.runId;
        state.latestResult = action.payload.data;
      })
      .addCase(loadLatestLocatorResultAsync.rejected, (state, action) => {
        state.resultError = action.payload;
      })
      .addCase(deleteLocatorHistoryFileAsync.fulfilled, (state, action) => {
        if (state.activeRunId === action.payload) {
          state.activeRunId = null;
          state.latestResult = null;
        }
      });
  },
});

export default locatorHistorySlice.reducer;
